import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Ticket, 
  Tag, 
  ArrowRight, 
  Calendar, 
  DollarSign,
  ShoppingCart,
  TrendingUp,
  Search,
  Filter,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ResaleMarketplace() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedListing, setSelectedListing] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [activeTab, setActiveTab] = useState('browse');
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const [paypalConfig, setPaypalConfig] = useState(null);
  const paypalButtonRef = useRef(null);

  useEffect(() => {
    fetchListings();
    fetchPaypalConfig();
    if (isAuthenticated) {
      fetchMyListings();
    }
    
    // Handle PayPal return
    const success = searchParams.get('success');
    const listingId = searchParams.get('listing_id');
    if (success === 'true' && listingId) {
      toast.success('Payment completed! Your ticket is now in My Tickets.');
      // Clear URL params
      window.history.replaceState({}, '', '/resale');
    }
  }, [isAuthenticated, searchParams]);

  const fetchPaypalConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/payments/paypal/config`);
      setPaypalConfig(response.data);
    } catch (error) {
      console.error('Error fetching PayPal config:', error);
    }
  };

  // Load PayPal SDK when modal opens
  useEffect(() => {
    if (showPurchaseModal && selectedListing && paypalConfig && !paypalLoaded) {
      loadPaypalScript();
    }
  }, [showPurchaseModal, selectedListing, paypalConfig]);

  const loadPaypalScript = () => {
    if (document.getElementById('paypal-resale-script')) {
      renderPaypalButtons();
      return;
    }

    const script = document.createElement('script');
    script.id = 'paypal-resale-script';
    script.src = `https://www.paypal.com/sdk/js?client-id=${paypalConfig.client_id}&currency=USD`;
    script.onload = () => {
      setPaypalLoaded(true);
      renderPaypalButtons();
    };
    document.body.appendChild(script);
  };

  const renderPaypalButtons = () => {
    if (!window.paypal || !paypalButtonRef.current || !selectedListing) return;
    
    // Clear previous buttons
    paypalButtonRef.current.innerHTML = '';

    window.paypal.Buttons({
      style: {
        layout: 'vertical',
        color: 'gold',
        shape: 'rect',
        label: 'paypal'
      },
      createOrder: async () => {
        try {
          const response = await axios.post(`${API_URL}/resale/${selectedListing.id}/create-order`);
          return response.data.order_id;
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to create order');
          throw error;
        }
      },
      onApprove: async (data) => {
        setPurchasing(true);
        try {
          const response = await axios.post(`${API_URL}/resale/${selectedListing.id}/capture-order`, {
            orderID: data.orderID
          });
          toast.success('Ticket purchased successfully!');
          setShowPurchaseModal(false);
          setSelectedListing(null);
          fetchListings();
          if (isAuthenticated) fetchMyListings();
          navigate('/my-tickets');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Payment failed');
        } finally {
          setPurchasing(false);
        }
      },
      onError: (err) => {
        console.error('PayPal error:', err);
        toast.error('Payment failed. Please try again.');
      },
      onCancel: () => {
        toast.info('Payment cancelled');
      }
    }).render(paypalButtonRef.current);
  };

  // Re-render PayPal buttons when listing changes
  useEffect(() => {
    if (paypalLoaded && selectedListing && showPurchaseModal) {
      renderPaypalButtons();
    }
  }, [selectedListing, paypalLoaded, showPurchaseModal]);

  useEffect(() => {
    fetchListings();
    if (isAuthenticated) {
      fetchMyListings();
    }
  }, [isAuthenticated]);

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API_URL}/resale/listings`);
      setListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyListings = async () => {
    try {
      const response = await axios.get(`${API_URL}/resale/my-listings`);
      setMyListings(response.data);
    } catch (error) {
      console.error('Error fetching my listings:', error);
    }
  };

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to purchase');
      navigate('/login');
      return;
    }

    if (!selectedListing) return;
    
    // Open modal - PayPal will handle the actual purchase
    setShowPurchaseModal(true);
  };

  const handleCancelListing = async (listingId) => {
    try {
      await axios.delete(`${API_URL}/resale/${listingId}`);
      toast.success('Listing cancelled');
      fetchListings();
      fetchMyListings();
    } catch (error) {
      toast.error('Failed to cancel listing');
    }
  };

  const filteredListings = listings.filter(l => 
    l.event_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.tier_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
              <Tag className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl">Resale Marketplace</h1>
              <p className="text-muted-foreground">Buy and sell tickets from other fans</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-white/10">
          <button
            className={`pb-3 px-1 font-medium transition-colors ${
              activeTab === 'browse' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('browse')}
            data-testid="resale-browse-tab"
          >
            Browse Listings
          </button>
          {isAuthenticated && (
            <button
              className={`pb-3 px-1 font-medium transition-colors ${
                activeTab === 'my-listings' 
                  ? 'text-primary border-b-2 border-primary' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setActiveTab('my-listings')}
              data-testid="resale-my-listings-tab"
            >
              My Listings ({myListings.filter(l => l.status === 'active').length})
            </button>
          )}
        </div>

        {activeTab === 'browse' && (
          <>
            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Search by event or ticket type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="resale-search"
                />
              </div>
            </div>

            {/* Listings Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-card border border-white/10 rounded-xl p-6 animate-pulse">
                    <div className="h-5 bg-white/10 rounded w-3/4 mb-3" />
                    <div className="h-4 bg-white/10 rounded w-1/2 mb-4" />
                    <div className="h-8 bg-white/10 rounded w-1/3" />
                  </div>
                ))}
              </div>
            ) : filteredListings.length === 0 ? (
              <div className="text-center py-16 bg-card border border-white/10 rounded-xl">
                <Tag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-xl mb-2">No listings found</h3>
                <p className="text-muted-foreground mb-6">
                  {searchTerm ? 'Try a different search term' : 'There are no tickets for resale at the moment'}
                </p>
                <Link to="/events">
                  <Button>Browse Events</Button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredListings.map((listing) => (
                  <div 
                    key={listing.id}
                    className="bg-card border border-white/10 rounded-xl overflow-hidden hover:border-primary/30 transition-all group"
                    data-testid={`resale-listing-${listing.id}`}
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <Badge variant="secondary" className="text-xs">
                          {listing.tier_name}
                        </Badge>
                        {listing.asking_price < listing.base_price * 1.3 && (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            <TrendingUp className="w-3 h-3 mr-1" />
                            Good Deal
                          </Badge>
                        )}
                      </div>
                      
                      <h3 className="font-heading font-semibold text-lg mb-2 line-clamp-2">
                        {listing.event_title}
                      </h3>
                      
                      <p className="text-sm text-muted-foreground mb-4">
                        Sold by {listing.seller_name}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-muted-foreground">Asking price</p>
                          <p className="font-heading font-bold text-2xl text-primary">
                            ${listing.asking_price}
                          </p>
                        </div>
                        <Button
                          onClick={() => {
                            setSelectedListing(listing);
                            setShowPurchaseModal(true);
                          }}
                          className="gap-2"
                          data-testid={`buy-listing-${listing.id}`}
                        >
                          <ShoppingCart className="w-4 h-4" />
                          Buy
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'my-listings' && isAuthenticated && (
          <div className="space-y-4">
            {myListings.length === 0 ? (
              <div className="text-center py-16 bg-card border border-white/10 rounded-xl">
                <Ticket className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-xl mb-2">No listings yet</h3>
                <p className="text-muted-foreground mb-6">
                  You can list tickets for resale from your tickets page
                </p>
                <Link to="/my-tickets">
                  <Button>Go to My Tickets</Button>
                </Link>
              </div>
            ) : (
              myListings.map((listing) => (
                <div 
                  key={listing.id}
                  className="bg-card border border-white/10 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                  data-testid={`my-listing-${listing.id}`}
                >
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-heading font-semibold">{listing.event_title}</h3>
                      <Badge 
                        className={
                          listing.status === 'active' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                          listing.status === 'sold' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                          'bg-gray-500/20 text-gray-400 border-gray-500/30'
                        }
                      >
                        {listing.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{listing.tier_name}</p>
                    <p className="text-sm text-muted-foreground">
                      Listed: {format(new Date(listing.created_at), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Asking price</p>
                      <p className="font-heading font-bold text-xl text-primary">${listing.asking_price}</p>
                    </div>
                    {listing.status === 'active' && (
                      <Button 
                        variant="outline"
                        onClick={() => handleCancelListing(listing.id)}
                        data-testid={`cancel-listing-${listing.id}`}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Purchase Modal */}
      <Dialog open={showPurchaseModal} onOpenChange={(open) => {
        setShowPurchaseModal(open);
        if (!open) setSelectedListing(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Purchase Resale Ticket</DialogTitle>
            <DialogDescription>
              Complete your purchase via PayPal
            </DialogDescription>
          </DialogHeader>
          {selectedListing && (
            <div className="space-y-4 py-4">
              <div className="bg-secondary/30 rounded-lg p-4">
                <h3 className="font-heading font-semibold text-lg mb-1">
                  {selectedListing.event_title}
                </h3>
                <p className="text-sm text-muted-foreground">{selectedListing.tier_name}</p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Seller</span>
                  <span>{selectedListing.seller_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Seller receives (70%)</span>
                  <span>${selectedListing.base_price?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Platform fee (30%)</span>
                  <span className="text-muted-foreground">${selectedListing.commission?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold text-lg pt-2 border-t border-white/10">
                  <span>Total</span>
                  <span className="text-primary">${selectedListing.asking_price?.toFixed(2)}</span>
                </div>
              </div>

              {/* PayPal Buttons */}
              <div className="pt-4">
                {purchasing ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    <span className="ml-2">Processing payment...</span>
                  </div>
                ) : (
                  <div ref={paypalButtonRef} data-testid="paypal-resale-buttons">
                    {!paypalLoaded && (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                        <span className="ml-2 text-muted-foreground">Loading PayPal...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <p className="text-xs text-center text-muted-foreground">
                By purchasing, you agree to our terms of service. Platform fee (30%) is included in the price.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
